import { Badge, Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { AddRegular, DocumentBulletListRegular, MoreHorizontalRegular, OpenRegular, ReceiptRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader, CellMono, CellDate, TableStatusBadge } from '../common';
import type { DataTableColumn } from '../common';
import { AsyncList } from '../common/AsyncList';
import { useInstallations, type Installation } from './useInstallations';
import { useProducts } from '../products/useReleases';
import { installBadge, safeSiteUrl } from '../../releases/installCounts';

/** Latest release per Guardian app id, from the product list (used when Guardian does not send latestVersion). */
type LatestByApp = Record<string, string | null>;

function VersionCell({ i, latest }: { i: Installation; latest: LatestByApp }) {
  const badge = installBadge(i, latest[i.productId] ?? null);
  return (
    <span>
      <CellMono>{i.version || '—'}</CellMono>{' '}
      {badge?.kind === 'withdrawn' && <Badge appearance="tint" color="danger">withdrawn build</Badge>}
      {badge?.kind === 'update' && (
        <Badge appearance="tint" color="warning">{badge.target ? `→ ${badge.target} available` : 'update available'}</Badge>
      )}
    </span>
  );
}

function RowMenu({ i }: { i: Installation }) {
  const navigate = useNavigate();
  const site = safeSiteUrl(i.siteUrl);
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button size="small" appearance="subtle" icon={<MoreHorizontalRegular />} aria-label={`Actions for ${i.customerName} ${i.productName}`} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<ReceiptRegular />} disabled={!i.deploymentId} onClick={() => i.deploymentId && navigate(`/deploy/${encodeURIComponent(i.deploymentId)}`)}>
            Open session
          </MenuItem>
          <MenuItem icon={<OpenRegular />} disabled={!site} onClick={() => { if (site) window.open(site, '_blank', 'noopener,noreferrer'); }}>
            Open site
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function buildColumns(latest: LatestByApp): DataTableColumn<Installation>[] {
  return [
    { key: 'customer', header: 'Customer', render: (i) => i.customerName },
    { key: 'product', header: 'Product', render: (i) => i.productName },
    { key: 'version', header: 'Version', render: (i) => <VersionCell i={i} latest={latest} /> },
    { key: 'site', header: 'Site', render: (i) => i.siteUrl },
    { key: 'state', header: 'State', render: (i) => <TableStatusBadge status={i.state} /> },
    { key: 'updated', header: 'Updated', render: (i) => <CellDate date={i.updatedAt} /> },
    { key: 'actions', header: '', width: '1%', render: (i) => <RowMenu i={i} /> },
  ];
}

export function DeploymentsPage() {
  const navigate = useNavigate();
  const installations = useInstallations();
  const products = useProducts();
  const latest: LatestByApp = {};
  (products.data ?? []).forEach((p) => { latest[p.guardianAppId] = p.release?.currentVersion ?? null; });
  const columns = buildColumns(latest);

  return (
    <div>
      <PageHeader
        title="Deployments"
        description="Every FirstIQ product installed in a customer tenant, and where it stands."
        actions={
          <Button appearance="primary" icon={<AddRegular />} onClick={() => navigate('/deploy')}>
            New deployment
          </Button>
        }
      />
      <AsyncList
        state={installations}
        columns={columns}
        keyExtractor={(i) => `${i.tenantId}:${i.productId}`}
        loadingLabel="Loading installation records..."
        empty={{
          icon: <DocumentBulletListRegular />,
          title: 'No deployment records yet',
          description:
            'Installation records from FirstIQ Guardian (customer, product, version, site and state) will appear here once the Deployment Manager reads them from Guardian.',
        }}
      />
    </div>
  );
}
